import React, { useState, useRef, useEffect } from 'react'
import Box from '@mui/material/Box'
import Avatar from '@mui/material/Avatar'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInput from 'components/custom/AdxInput'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'

import AdxAutocomplete from 'components/reusable/AdxAutocomplete'
import defaultAvatar from 'assets/images/default_avatar.png'

import {
  getApproverGroupStaff,
  postApproverGroupStaff,
  putApproverGroupStaff,
  getApproverGroupStaffById,
  getApproverGroupById,
} from 'services/api/admin-accessibility'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { handleValidationErrors } from 'services/helper/handleValidationErrors'
import { renderCountBadge } from 'components/custom/AdxLabelRenderer'
import AdxButton from 'components/custom/AdxButton'

const AddCustomGroupModal = ({
  showAddCustomGroupModal,
  handleCloseAddCustomGroupModal,
  selectedCustomCardId,
  selectedItem,
  disableInput,
  approverGroupStaffList,
  dropDown,
  setDropDown,
}) => {
  const [selectValue, setSelectValue] = useState('')
  const formikRef = useRef()
  const queryClient = useQueryClient()

  const [initialValues, setInitialValues] = useState({
    name: '',
    description: '',
    staff: [],
  })

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    staff: Yup.array().required().min(1, 'Atleast 1 member is required to create a group'),
  })

  const [selectedStaff, setSelectedStaff] = useState([])

  const handleSearchChange = (e, value) => {
    let data = [value, ...selectedStaff]
    setSelectedStaff(data)
    let arrayOfDataId = data.map((item) => item.id)
    formikRef.current.setFieldValue('staff', arrayOfDataId)
  }

  const filterArray = () => {
    let dummyApproverGroupStaffList = approverGroupStaffList?.data?.map((item) => item.id)
    let dummySelectedStaff = selectedStaff?.map((item) => item.id)
    let filteredArray = dummyApproverGroupStaffList?.filter((x) => !dummySelectedStaff?.includes(x))
    const filterNew = approverGroupStaffList?.data
      ?.map((item) => filteredArray?.includes(item.id) && item)
      .filter((el) => el)
    setDropDown(filterNew)
  }

  useEffect(() => {
    filterArray()
  }, [selectedCustomCardId, selectedStaff])

  const mutate = useMutation(
    async (data) => {
      if (!selectedCustomCardId) {
        await postApproverGroupStaff(data)
      } else {
        await putApproverGroupStaff(selectedCustomCardId, data)
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('approverGroupList')
        setSelectedStaff([])
        handleCloseModal()
      },
      onError: async (e) => {
        if (e.status === 422) {
          await handleValidationErrors(e, formikRef)
        } else {
          console.log('error here', e.data.message)
        }
      },
    }
  )

  const handleOnSubmit = (values, actions) => {
    mutate.mutate(values)
  }

  const handleCloseModal = () => {
    handleCloseAddCustomGroupModal()
    setInitialValues({
      name: '',
      description: '',
      staff: [],
    })
    setSelectedStaff([])
  }

  const handleDelete = (item) => {
    let array = [...selectedStaff]
    const index = array.indexOf(item)
    if (index > -1) {
      array.splice(index, 1)
    }
    setSelectedStaff(array)
    let arrayOfDataId = array.map((item) => item.id)
    formikRef.current.setFieldValue('staff', arrayOfDataId)
  }

  const approvalGroupById = async () => {
    try {
      const res = await getApproverGroupById(selectedCustomCardId)
      setInitialValues({
        name: res?.data?.group_name,
        description: res?.data?.group_description,
        staff: res?.data?.members.map((item) => item.id),
      })
      setSelectedStaff(res?.data?.members)
    } catch (e) {
      console.log('error')
    }
  }

  useEffect(() => {
    if (selectedCustomCardId) {
      approvalGroupById()
    }
  }, [selectedCustomCardId])

  return (
    <Formik
      enableReinitialize
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={(values, actions) => handleOnSubmit(values, actions)}
      validationSchema={validationSchema}
    >
      {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldTouched }) => (
        <Form>
          <AdxFormModal
            title="Admin group"
            subtitle=""
            open={showAddCustomGroupModal}
            onClose={handleCloseModal}
            enableFooter
            actionText={selectedCustomCardId ? 'Save' : 'Create'}
            handleAction={() => formikRef.current.submitForm()}
            handleClose={handleCloseModal}
            actionButtonDisabled={disableInput}
            sxModal={{ minWidth: { xs: 1, md: 700 } }}
          >
            <Box sx={{ minHeight: '700px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography>Group name</Typography>
                <Field
                  as={AdxInput}
                  name="name"
                  size="standard"
                  placeholder="Max character 10"
                  charLimit={30}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={<ErrorMessage name="name" component={AdxErrorMessage} />}
                  disabled={disableInput}
                  isErrorShown={errors.name && touched.name}
                />
              </Box>
              <Box item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography>Group description (optional)</Typography>
                <Field
                  as={AdxInput}
                  name="description"
                  size="standard"
                  placeholder="Max character 120"
                  charLimit={120}
                  fullWidth
                  multiline
                  minRows={3}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={<ErrorMessage name="description" component={AdxErrorMessage} />}
                  disabled={disableInput}
                  isErrorShown={errors.description && touched.description}
                />
              </Box>
              <Box item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography>Group Members</Typography>
                  {renderCountBadge(values?.staff?.length)}
                </Box>
                <Field
                  as={AdxAutocomplete}
                  name="staff"
                  onBlur={() => setFieldTouched('staff', true)}
                  onChange={(e, value) => handleSearchChange(e, value)}
                  options={dropDown}
                  icon="search"
                  getOptionLabel={(option) => option.name || ''}
                  disabled={disableInput}
                  disableClearable
                  placeholder="Select Admin"
                  isErrorShown={errors.staff && touched.staff}
                  errorMessage={<ErrorMessage name="staff" component={AdxErrorMessage} />}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedStaff.map((item) => (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', lg: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', lg: 'center' },
                        bgcolor: 'alternate.light',
                        borderRadius: '4.5px',
                        p: 2,
                      }}
                    >
                      <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }}>
                        <Box display="flex" alignItems="center" gap={2} width={{ xs: '100%' }}>
                          <Avatar
                            src={item?.profile_image_url || defaultAvatar}
                            width="45px"
                            height="45px"
                            sx={{ objectFit: 'cover' }}
                            borderRadius="50%"
                          />
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="left"
                            justifyContent="center"
                            flex={1}
                            gap={0.5}
                          >
                            <Typography variant="body1">{item?.name}</Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 1,
                                alignItems: 'center',
                              }}
                            >
                              <Typography variant="body2" color="light-gray-text.main">
                                {item?.designation}
                              </Typography>
                              <Icon
                                sx={{
                                  fontSize: 7,
                                  color: 'light-gray-text.main',
                                }}
                              >
                                circle_rounded
                              </Icon>
                              <Typography variant="body2" color="light-gray-text.main">
                                {item?.noOfModules} modules
                              </Typography>
                              <Icon
                                sx={{
                                  fontSize: 7,
                                  color: 'light-gray-text.main',
                                }}
                              >
                                circle_rounded
                              </Icon>
                              <Typography variant="body2" color="light-gray-text.main">
                                Already in {item?.existing_groups_count} group
                                {item.existing_groups_count > 1 && 's'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      {!disableInput && (
                        <AdxButton
                          variant={'ghost'}
                          color="error"
                          onClick={() => handleDelete(item)}
                        >
                          Remove
                        </AdxButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
              {/* {!disableInput && (
                <Box width={{ lg: '90%' }} mt={2}>
                  <Grid container>
                    <Grid item xs={12} lg={4}>
                      <AdxButton variant={'ghost'} fullWidth onClick={handleCloseAddCustomGroupModal}>
                        Cancel
                      </AdxButton>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                      <AdxButton fullWidth onClick={handleSubmit}>
                        {selectedCustomCardId ? 'Save' : 'Create'}
                      </AdxButton>
                    </Grid>
                  </Grid>
                </Box>
              )} */}
            </Box>
          </AdxFormModal>
        </Form>
      )}
    </Formik>
  )
}

export default AddCustomGroupModal
